import { BusinessOutlined, MemoryOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Stack,
  Typography
} from "@mui/material";
import { COLORS, disabledContainedButtonSx } from "assets";
import { MyModal, TextInput } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { MouseEventHandler, useCallback, useEffect, useState } from "react";
import { BoxModel } from "types";
import { getSiteLabel } from "utils";

interface IRemoveBox {
  removing: BoxModel;
  onSuccess?: () => void;
  onClose: () => void;
}

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

const cancelSx = {
  minWidth: 120,
  height: 44,
  borderRadius: `${COLORS.radius.md}px`,
  borderColor: COLORS.surface.border,
  color: COLORS.surface.primaryText,
  fontWeight: 500,
  textTransform: "none"
};

const removeSx = {
  minWidth: 140,
  height: 44,
  borderRadius: `${COLORS.radius.md}px`,
  fontWeight: 500,
  textTransform: "none",
  bgcolor: COLORS.error,
  color: COLORS.white,
  boxShadow: "none",
  "&:hover": {
    bgcolor: COLORS.error,
    filter: "brightness(1.05)",
    boxShadow: "none"
  },
  "&.Mui-disabled": disabledContainedButtonSx
};

export default function RemoveBox({ removing, onClose, onSuccess }: IRemoveBox) {
  const [checked, setChecked] = useState(false);
  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchDelete } = useFetch("DELETE");
  const { enqueueSnackbar } = useSnackbar();
  const onSubmit: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchDelete(ENDPOINTS.boxes + `/${removing._id}`).catch(console.error);
    },
    [removing, callFetchDelete]
  );
  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onClose();
      onSuccess?.();
    }
  }, [response, onClose, enqueueSnackbar, onSuccess]);
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  return (
    <MyModal
      title={t["Remove Smart Box"]}
      subtitle={t["Are You Sure You Want to Remove?"]}
      open={true}
      setOpen={onClose}
      variant="light"
    >
      <Stack spacing={2.5}>
        <TextInput
          value={removing?.deviceId || ""}
          label={t["Device ID"]}
          type="text"
          disabled
          startAdornment={<MemoryOutlined sx={iconSx} />}
          sx={fieldSx}
          fullWidth
        />
        <TextInput
          value={getSiteLabel(removing.site)}
          label={t["Site"]}
          type="text"
          disabled
          startAdornment={<BusinessOutlined sx={iconSx} />}
          sx={fieldSx}
          fullWidth
        />

        <FormControl fullWidth>
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(_, value) => setChecked(value)}
                sx={{
                  color: COLORS.surface.secondaryText,
                  "&.Mui-checked": { color: COLORS.error }
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: 14, color: COLORS.surface.secondaryText, lineHeight: 1.5 }}>
                {t["I confirm that removing this is an irreversible action and i want to proceed."]}
              </Typography>
            }
          />
        </FormControl>
      </Stack>

      <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
        <Button type="button" variant="outlined" onClick={onClose} disabled={isLoading} sx={cancelSx}>
          {t["Cancel"]}
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={!checked || isLoading}
          endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
          onClick={onSubmit}
          sx={removeSx}
        >
          {t["Remove"]}
        </Button>
      </Box>
    </MyModal>
  );
}
